import { FC, useCallback } from "react";
import { useMemo } from "react";
import React from "react";
import { FormRenderProps, useForm, useFormState } from "react-final-form";
import { Field } from "react-final-form";
import Typography from "@material-ui/core/Typography";
import Loading from "../../../../components/Loading";
import { Grid } from "@material-ui/core";
import BootstrapInput from "../../../../components/BootstrapInput";
import Autocomplete from "../../../../components/Autocomplete";
import PasswordInput from "../../../../components/PasswordInput";
import Button from "../../../../components/Button";
import { getData } from "country-list";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { Theme } from "@material-ui/core/styles";
import TermsApproval from "../TermsApproval";
import UploadFile from "../../../../components/UploadFile/UploadFile";
import TextDivider from "../../../../components/TextDivider";
import { AccordionList } from "../../../../components/AccordionList";
import { teacherDetailsConfig } from "../../../privateClasses/constants";

const useStyles = makeStyles((theme: Theme) => ({
  fieldsContainer: {
    marginBottom: -4,
    "& .MuiGrid-item": {
      padding: 8,
    },
  },
  divider: {
    marginTop: 24,
  },
  uploadContainer: {
    height: 200,
    alignItems: "start",
    marginTop: 12,
  },
  recaptchaText: {
    marginTop: 8,
  },
}));

const RegisterTeacherForm: FC<FormRenderProps> = ({
  handleSubmit,
  submitError,
  submitting,
}) => {
  const classes = useStyles();
  const { values } = useFormState();
  const { change } = useForm();

  const countries = useMemo(
    () =>
      getData().sort((a, b) =>
        a.name > b.name ? 1 : b.name > a.name ? -1 : 0,
      ),
    [],
  );

  const files = useMemo(
    () => (values.image ? [values.image] : []),
    [values.image],
  );

  const certificateFiles = useMemo(
    () => values.certificates || [],
    [values.certificates],
  );

  const cvFiles = useMemo(() => (values.cv ? [values.cv] : []), [values.cv]);

  const addNewFiles = useCallback(
    async (files) => {
      const file = files[0];

      change("image", {
        id: 1,
        name: file.name,
        size: file.size,
        file,
      });
    },
    [change],
  );

  const removeFileHandler = useCallback(() => {
    change("image", null);
  }, [change]);

  const addCertificateFiles = useCallback(
    async (newFiles) => {
      const current = values.certificates || [];
      const mapped = newFiles.map((file, i) => ({
        id: current.length + i + 1,
        name: file.name,
        size: file.size,
        file,
      }));
      change("certificates", [...current, ...mapped]);
    },
    [change, values.certificates],
  );

  const removeCertificateFile = useCallback(
    (file: any) => {
      const current: any[] = values.certificates || [];
      change(
        "certificates",
        current.filter((f) => f.id !== file.id),
      );
    },
    [change, values.certificates],
  );

  const addCvFile = useCallback(
    async (files) => {
      const file = files[0];
      change("cv", {
        id: 1,
        name: file.name,
        size: file.size,
        file,
      });
    },
    [change],
  );

  const removeCvFile = useCallback(() => {
    change("cv", null);
  }, [change]);

  return (
    <form noValidate onSubmit={handleSubmit}>
      {submitError && <Typography color="error">{submitError}</Typography>}
      {submitting && <Loading />}

      <Grid container spacing={3} className={classes.fieldsContainer}>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="name"
            label="Full Name"
            placeholder="John Smith"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="phone"
            label="Phone Number"
            placeholder="+19159969739"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            name="teacherProfile.country"
            label="Country"
            options={countries}
            placeholder="Please Select"
            component={Autocomplete}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="email"
            label="Email"
            placeholder="example@test.com"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            name="password"
            label="Password"
            placeholder="Must have at least 8 characters"
            autoComplete="new-password"
            component={PasswordInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            label="Repeat Password"
            placeholder="Must have at least 8 characters"
            name="password_confirmation"
            component={PasswordInput}
          />
        </Grid>
      </Grid>

      <TextDivider text={"Details"} className={classes.divider} />

      <AccordionList
        fieldName="teacherProfile.details"
        configs={teacherDetailsConfig}
      />

      <Grid
        container
        spacing={3}
        alignItems={"center"}
        justify={"flex-end"}
        className={classes.uploadContainer}
      >
        <Grid item sm={4} xs={12}>
          <UploadFile
            uploadedFiles={certificateFiles}
            addNewFiles={addCertificateFiles}
            removeFile={removeCertificateFile}
            titleText={"Upload Certificates"}
            subText={"Drag & Drop Your Certificates"}
            allowedFileTypes={["pdf", "jpg", "jpeg", "png"]}
            maxSize={5}
            maxFiles={5}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <UploadFile
            required
            name="cv"
            uploadedFiles={cvFiles}
            addNewFiles={addCvFile}
            removeFile={removeCvFile}
            titleText={"Upload CV"}
            subText={"Drag & Drop Your CV"}
            allowedFileTypes={["pdf", "jpg", "jpeg", "png"]}
            maxSize={5}
            maxFiles={1}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <UploadFile
            required
            uploadedFiles={files}
            addNewFiles={addNewFiles}
            removeFile={removeFileHandler}
            titleText={"Upload Profile Picture"}
            subText={"Drag & Drop Your Picture"}
            allowedFileTypes={["jpg", "jpeg", "png"]}
            maxSize={5}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} alignItems={"center"} justify={"flex-end"}>
        <Grid item sm={4} xs={12}>
          <TermsApproval termsText={""} />
          <Button
            fullWidth
            type="submit"
            color="secondary"
            variant="contained"
            disabled={submitting}
          >
            Sign Up
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={3} alignItems={"center"} justify={"flex-end"}>
        <Grid item sm={4} xs={12}>
          <Typography
            component="p"
            align="center"
            className={classes.recaptchaText}
          >
            This site is protected by reCAPTCHA and the Google{" "}
            <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
            <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
            apply.
          </Typography>
        </Grid>
      </Grid>
    </form>
  );
};

export default RegisterTeacherForm;
